import { ArrowLeftIcon } from 'lucide-react';
import _ from 'lodash';
import { makeAutoObservable, runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { Link } from 'react-router';
import { BlogPostResponse } from '../../../server/response/blogResponseScheme';
import { BLOG_LANGS, BlogLangType } from '../../../types/blogTypes';
import { Hydratable } from '../../../types/ssrTypes';
import { useRegionTime } from '../../blogHooks/useRegionTime';
import { useSsr } from '../../ssr/SsrProvider';
import Box from '../../uikit/Box';
import Frame from '../../uikit/Frame';
import { blogApiClient } from '../../utils/blogApiClient';
import { useMobxInstance } from '../../utils/mobxUtil';
import YoutubePlayer from '../../views/common/YoutubePlayer';
import BlogJsonld from '../BlogJsonld';
import BlogMetaHeader from '../BlogMetaHeader';
import { useBlogContext } from '../BlogProvider';
import { BlogCategoryBadge } from '../list/BlogCategory';
import BlogPostList from '../list/BlogPostList';
import BlogContentViewer from './BlogContentViewer';
import { BlogImageSearch } from './BlogImageSearch';

class ViewStore implements Hydratable<'BlogPostDetailPage'> {
    lang: BlogLangType | undefined;
    category: string | undefined;
    slug: string | undefined;
    blogPost: BlogPostResponse | null = null;

    isEditing = false;

    constructor(
        lang: BlogLangType | undefined,
        category: string | undefined,
        slug: string | undefined,
    ) {
        makeAutoObservable(this);

        this.lang = lang;
        this.category = category;
        this.slug = slug;
    }

    hydrate(data: BlogPostResponse) {
        blogApiClient.blogPost.getBlogPostBySlug.$setCache(
            {
                lang: this.lang!,
                slug: this.slug as string,
            },
            data,
        );

        this.blogPost = data;
    }

    get isValid() {
        const isValidParam =
            BLOG_LANGS.includes(this.lang as BlogLangType) && this.category && this.slug;
        return isValidParam;
    }

    async fetch() {
        if (!this.isValid) {
            return;
        }

        const blogPost = await blogApiClient.blogPost.getBlogPostBySlug.$query({
            lang: this.lang!,
            slug: this.slug as string,
        });

        runInAction(() => {
            this.blogPost = blogPost || null;
        });
    }

    async saveImages(images: string[]) {
        if (!this.blogPost) {
            return;
        }

        await blogApiClient.blogPost.saveBlogPostImage.mutate({
            postHash: this.blogPost.hash,
            images,
        });

        const updated = await blogApiClient.blogPost.getBlogPostBySlug.$forceQuery({
            lang: this.lang!,
            slug: this.slug as string,
        });

        runInAction(() => {
            this.blogPost = updated || null;
            this.isEditing = false;
        });
    }

    setIsEditing(value: boolean) {
        this.isEditing = value;
    }
}

const BlogPostDetailPage = observer(() => {
    const { lang, category, slug } = useBlogContext();

    const hasPermission = import.meta.env.DEV;

    const viewStore = useMobxInstance(
        ViewStore,
        lang as BlogLangType,
        category as string,
        slug as string,
    );

    useSsr(
        'BlogPostDetailPage',
        {
            lang: lang as BlogLangType,
            category: category as string,
            slug: slug as string,
        },
        viewStore,
    );

    const publishedAt = useRegionTime(
        viewStore.blogPost?.publishedAt || '',
        viewStore.lang || 'ko',
        'YYYY-MM-DD HH:mm',
    );

    useEffect(() => {
        viewStore.fetch();
    }, [viewStore]);

    if (!viewStore.isValid) {
        return <div>Invalid</div>;
    }

    return (
        <Frame>
            <BlogMetaHeader
                title={viewStore.blogPost?.title}
                description={viewStore.blogPost?.seoDescription || ''}
                image={viewStore.blogPost?.images?.[0] || ''}
            />
            <BlogJsonld
                type="BlogPosting"
                title={viewStore.blogPost?.title}
                description={viewStore.blogPost?.seoDescription || ''}
                image={viewStore.blogPost?.images?.[0] || ''}
                publishedAt={publishedAt}
            />
            <Frame.Title title={viewStore.blogPost?.title} isShowOnlyHeader={true}></Frame.Title>
            <Box>
                <article>
                    {/* 헤더 영역 */}
                    <header>
                        <div className="flex flex-row gap-4 text-sm text-gray-500">
                            <BlogCategoryBadge category={viewStore.blogPost?.category || ''} />
                            <time dateTime={publishedAt}>{publishedAt}</time>
                        </div>
                        <h1 className="mt-5 mb-10 text-4xl leading-snug font-extrabold">
                            {viewStore.blogPost?.title}
                        </h1>
                    </header>
                    {/* 본문 영역 */}
                    {_.isEmpty(viewStore.blogPost?.images) ? null : (
                        <div className="mb-10">
                            <img
                                src={_.first(viewStore.blogPost?.images)}
                                alt={viewStore.blogPost?.title}
                                className="max-h-[500px] w-full object-cover"
                            />
                        </div>
                    )}
                    {viewStore.blogPost?.content && (
                        <BlogContentViewer content={viewStore.blogPost?.content} />
                    )}
                    {viewStore.blogPost?.youtubeVideoId && (
                        <div className="my-12">
                            <YoutubePlayer videoId={viewStore.blogPost.youtubeVideoId} />
                        </div>
                    )}
                    {viewStore.blogPost?.tickers && (
                        <section className="mt-12">
                            <h3 className="text-xl font-bold">관련 종목</h3>
                            <div className="mt-4 flex flex-row flex-wrap gap-2">
                                {viewStore.blogPost?.tickers.map((ticker) => (
                                    <a
                                        key={ticker}
                                        className="link"
                                        href={`https://finance.yahoo.com/quote/${ticker}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        {ticker}
                                    </a>
                                ))}
                            </div>
                        </section>
                    )}
                    {/* 부가 정보 */}
                    <footer>
                        {!_.isEmpty(viewStore.blogPost?.keywords) && (
                            <section className="mt-12 flex flex-row flex-wrap gap-2">
                                {viewStore.blogPost?.keywords?.map((keyword) => (
                                    <div
                                        key={keyword}
                                        className="badge badge-ghost badge-md link link-hover rounded-none px-3 py-3 text-base"
                                    >
                                        #{keyword}
                                    </div>
                                ))}
                            </section>
                        )}
                        {hasPermission && (
                            <div className="mt-12">
                                {viewStore.isEditing && (
                                    <BlogImageSearch
                                        onClickSave={async (images) => {
                                            await viewStore.saveImages(images);
                                        }}
                                    />
                                )}
                                {!viewStore.isEditing && (
                                    <div
                                        className="flex flex-row justify-end gap-2"
                                        onClick={() => viewStore.setIsEditing(true)}
                                    >
                                        <button className="btn btn-soft btn-sm">Edit</button>
                                    </div>
                                )}
                            </div>
                        )}
                    </footer>
                </article>
            </Box>
            <div className="my-2">
                <Link
                    to={`/${lang}/post/${viewStore.blogPost?.category}`}
                    className="link link-hover ml-2 flex flex-row items-center gap-2"
                >
                    <ArrowLeftIcon className="h-4 w-4" />
                    Back to list
                </Link>
            </div>
            {/*
            // 애드센스 승인까지 생략해둠
            <Box>
                <BlogAmazonAd />
            </Box> */}
            <Box>
                <Box.Title title="Related Articles" />
                <BlogPostList postHash={viewStore.blogPost?.hash} pageSize={5} />
            </Box>
        </Frame>
    );
});

export default BlogPostDetailPage;
